import React, { useState, useEffect } from 'react';
import { businessAPI } from '../../api/businesses';
import { serviceNamesAPI } from '../../api/serviceNames';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { resolveImageUrl } from '../../utils/formatters';

const AddServiceModal = ({ service, businessId, businessType, currencySymbol = '$', onClose, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [imageError, setImageError] = useState(false);

  const days = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];

  const getDefaultAvailability = () => ({
    monday: [{ start: '09:00', end: '22:00' }],
    tuesday: [{ start: '09:00', end: '22:00' }],
    wednesday: [{ start: '09:00', end: '22:00' }],
    thursday: [{ start: '09:00', end: '22:00' }],
    friday: [{ start: '09:00', end: '22:00' }],
    saturday: [],
    sunday: []
  });

  const parseAvailability = (value) => {
    if (!value) return null;
    if (typeof value === 'object') return value;
    if (typeof value === 'string') {
      try {
        return JSON.parse(value);
      } catch (e) {
        return null;
      }
    }
    return null;
  };

  const existingAvailability = parseAvailability(service?.availability);
  const [useBusinessHours, setUseBusinessHours] = useState(!existingAvailability);
  const [availability, setAvailability] = useState(existingAvailability || getDefaultAvailability());

  const [formData, setFormData] = useState({
    name: service?.name || '',
    description: service?.description || '',
    image: service?.image || '',
    duration_minutes: service?.duration_minutes || 30,
    price: service?.price || 0,
    buffer_before: service?.buffer_before || 0,
    buffer_after: service?.buffer_after || 0
  });
  const [serviceNames, setServiceNames] = useState([]);
  const [isCustomServiceName, setIsCustomServiceName] = useState(false);
  const [nameSelection, setNameSelection] = useState(service?.name || '');

  const selectValue = nameSelection || (serviceNames.length === 0 ? '__new__' : '');

  useEffect(() => {
    const loadNames = async () => {
      if (!businessType) return;

      try {
        const response = await serviceNamesAPI.getByBusinessType(businessType);
        const values = response.data.data || [];
        setServiceNames(values);

        if (service?.name) {
          const hasExisting = values.some((item) => item.name.toLowerCase() === service.name.toLowerCase());
          setIsCustomServiceName(!hasExisting);
          setNameSelection(hasExisting ? service.name : '__new__');
        } else {
          if (values.length === 0) {
            setIsCustomServiceName(true);
            setNameSelection('__new__');
            setFormData((prev) => ({ ...prev, name: '' }));
          } else if (!nameSelection) {
            setIsCustomServiceName(false);
            setNameSelection(values[0].name);
            setFormData((prev) => ({ ...prev, name: values[0].name }));
          }
        }
      } catch (error) {
        console.error('Failed to load service name suggestions:', error);
      }
    };

    loadNames();
  }, [businessType, service?.name]);

  useEffect(() => {
    if (service?.name) return;
    if (serviceNames.length === 0) {
      setIsCustomServiceName(true);
      setNameSelection('__new__');
      setFormData((prev) => ({ ...prev, name: '' }));
    } else if (!nameSelection) {
      setIsCustomServiceName(false);
      setNameSelection(serviceNames[0].name);
      setFormData((prev) => ({ ...prev, name: serviceNames[0].name }));
    }
  }, [service?.name, serviceNames.length]);

  const fileToDataUrl = (file) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

  const handleAddSlot = (day) => {
    setAvailability({
      ...availability,
      [day]: [...(availability[day] || []), { start: '09:00', end: '22:00' }]
    });
  };

  const handleRemoveSlot = (day, index) => {
    const next = [...(availability[day] || [])];
    next.splice(index, 1);
    setAvailability({ ...availability, [day]: next });
  };

  const handleSlotChange = (day, index, field, value) => {
    const next = [...(availability[day] || [])];
    next[index] = { ...next[index], [field]: value };
    setAvailability({ ...availability, [day]: next });
  };

  const handleToggleDay = (day, isOpen) => {
    if (isOpen) {
      setAvailability({ ...availability, [day]: [{ start: '09:00', end: '22:00' }] });
    } else {
      setAvailability({ ...availability, [day]: [] });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!businessId) {
      alert('Business ID not found. Please try again.');
      return;
    }

    const name = formData.name?.trim();
    if (!name) {
      alert('Service name is required.');
      return;
    }

    setLoading(true);
    try {
      if (businessType) {
        const exists = serviceNames.some(
          (item) => item.name.toLowerCase() === name.toLowerCase()
        );
        if (!exists) {
          await serviceNamesAPI.add({
            business_type: businessType,
            name
          });
        }
      }

      const payload = {
        ...formData,
        name,
        availability: useBusinessHours ? null : availability
      };

      if (service) {
        await businessAPI.updateService(businessId, service.id, payload);
        alert('Service updated successfully');
      } else {
        await businessAPI.createService(businessId, payload);
        alert('Service created successfully');
      }
      onSuccess();
    } catch (error) {
      console.error('Failed to save service:', error);
      alert('Failed to save service: ' + (error.response?.data?.message || 'Unknown error'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-hidden">
        <div className="flex justify-between items-center p-6 border-b">
          <h2 className="text-xl font-semibold">
            {service ? 'Edit Service' : 'Add New Service'}
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>
        
        <form onSubmit={handleSubmit} className="p-6 space-y-6 overflow-y-auto max-h-[calc(90vh-80px)]">
          <div>
            <label className="block text-sm font-medium mb-1">Service Name *</label>
            <select
              value={selectValue}
              onChange={(e) => {
                const value = e.target.value;
                if (value === '__new__') {
                  setIsCustomServiceName(true);
                  setNameSelection(value);
                  setFormData({ ...formData, name: '' });
                  return;
                }

                setIsCustomServiceName(false);
                setNameSelection(value);
                setFormData({ ...formData, name: value });
              }}
              className="w-full input"
            >
              {/* <option value="">Select service name</option> */}
              {serviceNames.map((item) => (
                <option key={item.id} value={item.name}>
                  {item.name}
                </option>
              ))}
              <option value="__new__">Add new service name</option>
            </select>

            <input
              type="text"
              required
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="w-full input mt-3"
              placeholder="Service name"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium mb-1">Description</label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              rows="3"
              className="w-full input"
              placeholder="Describe the service"
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">Service Image (Optional)</label>
            {formData.image ? (
              <div className="mb-3 flex items-center gap-3">
                {!imageError ? (
                  <img
                    src={typeof formData.image === 'string' ? resolveImageUrl(formData.image) : URL.createObjectURL(formData.image)}
                    alt={formData.name || 'Service'}
                    className="h-14 w-14 rounded-xl object-cover border bg-white"
                    onError={() => setImageError(true)}
                  />
                ) : (
                  <div className="h-14 w-14 rounded-xl border bg-gray-50 flex items-center justify-center text-xl">
                    ✂️
                  </div>
                )}
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={() => {
                    setFormData({ ...formData, image: '' });
                    setImageError(false);
                  }}
                >
                  Remove
                </button>
              </div>
            ) : null}
            <input
              type="file"
              accept="image/*"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (!file) return;
                setFormData({ ...formData, image: file });
                setImageError(false);
              }}
              className="w-full input"
            />
          </div>
          
          <div className="border rounded-lg p-4 space-y-3">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-sm font-medium">Service Time (Availability)</div>
                <div className="text-xs text-gray-500">Set hours for this service. For breaks, add multiple time slots in a day.</div>
              </div>
              <label className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  checked={useBusinessHours}
                  onChange={(e) => setUseBusinessHours(e.target.checked)}
                />
                <span className="text-sm">Use business hours</span>
              </label>
            </div>

            {!useBusinessHours && (
              <div className="space-y-4">
                {days.map((day) => (
                  <div key={day} className="border-t pt-3">
                    <div className="flex items-center justify-between mb-2">
                      <div className="text-sm font-semibold capitalize">{day}</div>
                      <label className="flex items-center space-x-2">
                        <span className="text-xs text-gray-600">Closed</span>
                        <button
                          type="button"
                          onClick={() => handleToggleDay(day, (availability[day] || []).length === 0)}
                          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                            (availability[day] || []).length > 0 ? 'bg-primary-600' : 'bg-gray-300'
                          }`}
                        >
                          <span
                            className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                              (availability[day] || []).length > 0 ? 'translate-x-6' : 'translate-x-1'
                            }`}
                          />
                        </button>
                        <span className="text-xs text-gray-600">Open</span>
                      </label>
                    </div>

                    {(availability[day] || []).length > 0 && (
                      <div className="space-y-2">
                        {(availability[day] || []).map((slot, index) => (
                          <div key={index} className="flex items-center space-x-2">
                            <input
                              type="time"
                              value={slot.start}
                              onChange={(e) => handleSlotChange(day, index, 'start', e.target.value)}
                              className="input w-28"
                            />
                            <span className="text-sm">to</span>
                            <input
                              type="time"
                              value={slot.end}
                              onChange={(e) => handleSlotChange(day, index, 'end', e.target.value)}
                              className="input w-28"
                            />
                            <button
                              type="button"
                              onClick={() => handleRemoveSlot(day, index)}
                              className="text-red-600 hover:text-red-800 text-sm"
                            >
                              Remove
                            </button>
                          </div>
                        ))}
                        <button
                          type="button"
                          onClick={() => handleAddSlot(day)}
                          className="text-sm text-primary-600 hover:text-primary-700"
                        >
                          + Add time slot
                        </button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">Duration (minutes) *</label>
            <input
              type="number"
              required
              min="5"
              max="1440"
              value={formData.duration_minutes}
              onChange={(e) => setFormData({ ...formData, duration_minutes: parseInt(e.target.value) })}
              className="w-full input"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium mb-1">Price ({currencySymbol}) *</label>
            <input
              type="number"
              required
              min="0"
              step="0.01"
              value={formData.price}
              onChange={(e) => setFormData({ ...formData, price: parseFloat(e.target.value) })}
              className="w-full input"
            />
          </div>
          
          {/* <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">Buffer Before (min)</label>
              <input
                type="number"
                min="0"
                max="120"
                value={formData.buffer_before}
                onChange={(e) => setFormData({ ...formData, buffer_before: parseInt(e.target.value) })}
                className="w-full input"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Buffer After (min)</label>
              <input
                type="number"
                min="0"
                max="120"
                value={formData.buffer_after}
                onChange={(e) => setFormData({ ...formData, buffer_after: parseInt(e.target.value) })}
                className="w-full input"
              />
            </div>
          </div> */}
          
          <div className="flex justify-end space-x-3 pt-4">
            <button type="button" onClick={onClose} className="btn-secondary">
              Cancel
            </button>
            <button type="submit" disabled={loading} className="btn-primary">
              {loading ? 'Saving...' : (service ? 'Update' : 'Create')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddServiceModal;
